export type AssetType = "image" | "video";
export type AdFormat = "Single Image or Video" | "Carousel" | "Collection" | "Catalog" | "Flexible";
export type AdStage = "Draft" | "Launched";
export type CollectionHeadlineType = "custom" | "macro";
export type CatalogHeadlineType = "macro" | "custom";
export type FlexibleAssetType = "image" | "video";

// Optional columns that can be toggled via "Add Dimensions" button
export type OptionalColumn = 
  | "partner_fb_page" 
  | "partner_instagram" 
  | "display_link" 
  | "descriptions"
  | "threads_page_id"
  | "leadgen_form"
  | "product_tags";

export const OPTIONAL_COLUMNS: { key: OptionalColumn; label: string }[] = [
  { key: "partner_fb_page", label: "Partner FB Page" },
  { key: "partner_instagram", label: "Partner Instagram" },
  { key: "display_link", label: "Display Link" },
  { key: "descriptions", label: "Descriptions" },
  { key: "threads_page_id", label: "Thread Profile" },
  { key: "leadgen_form", label: "Instant Form" },
  { key: "product_tags", label: "Product Tags" },
];

// Webhook format mapping for n8n compatibility
export const AD_FORMAT_MAP: Record<string, string> = {
  'Single Image or Video': 'single',
  'Single Image': 'single',
  'Single Video': 'single',
  'Carousel': 'carousel',
  'Collection': 'collection',
  'Catalog': 'catalog',
  'Flexible': 'flexible'
};

export interface CarouselCard {
  id: string;
  creative_id: string;
  creative_filename: string;
  creative_filename_vertical?: string | null;
  meta_creative_id: string;
  meta_creative_id_vertical?: string | null;
  thumbnail_url: string | null;
  headline: string;
  description?: string;
  card_url: string;
  cta_type?: string;
  creative_type: "image" | "video";
}

export interface CarouselData {
  cards: CarouselCard[];
}

export interface ProductSet {
  id: string;
  name: string;
  productCount?: number;
}

export interface Catalog {
  id: string;
  name: string;
  productSets: ProductSet[];
}

export interface CollectionData {
  creativeName: string;
  catalogId: string | null;
  catalogName: string | null;
  productSetId: string | null;
  productSetName: string | null;
  headlineType: CollectionHeadlineType;
  headlineValue: string;
  buttonLabel: string;
  destinationUrl: string;
  itemHeadline: string;
  itemDescription: string;
  descriptionType: "macro" | "custom";
}

export interface CatalogAdData {
  catalogId: string | null;
  catalogName: string | null;
  productSetId: string | null;
  productSetName: string | null;
  headlineType: CatalogHeadlineType;
  headlineValue: string;
  formatType: "carousel" | "single";
  primaryText: string;
  destinationUrl: string;
  ctaType: string;
  description?: string;
  descriptionType?: "macro" | "custom";
  descriptionValue?: string;
  automatedProductTags?: boolean;
}

export interface FlexibleAsset {
  id: string;
  meta_creative_id: string;
  filename: string;
  thumbnail_url: string | null;
  type: FlexibleAssetType;
}

export interface FlexibleData {
  assets: FlexibleAsset[];
  assetType: FlexibleAssetType;
}

export interface AdRow {
  id: string;
  adName: string;
  stage: AdStage;
  adFormat: AdFormat | null;
  adAccount: { id: string; name: string } | null;
  campaign: { id: string; name: string } | null;
  adSet: { id: string; name: string } | null;
  creatives: {
    feed: { id: string; filename: string; thumbnail: string | null; meta_creative_id: string; group_type?: string } | null;
    vertical: { id: string; filename: string; thumbnail: string | null; meta_creative_id: string; group_type?: string } | null;
    horizontal: { id: string; filename: string; thumbnail: string | null; meta_creative_id: string; group_type?: string } | null;
  } | null;
  carouselData: CarouselData | null;
  collectionData: CollectionData | null;
  catalogAdData: CatalogAdData | null;
  flexibleData: FlexibleData | null;
  // Direct catalog/product set fields (for Single Image/Video formats)
  catalogId: string | null;
  catalogName: string | null;
  productSetId: string | null;
  productSetName: string | null;
  facebookPage: { id: string; name: string } | null;
  instagramAccount: { id: string; username: string } | null;
  partnerFacebookPage: { id: string; username: string } | null;
  partnerInstagramAccount: { id: string; username: string } | null;
  primaryTexts: string[];
  headlines: string[];
  descriptions: string[];
  productTags: string[];
  url: string;
  displayLink: string;
  utmParameters: string;
  ctaType: string;
  status: "PAUSED" | "ACTIVE";
  threadsPageId: string;
  ad_template_id?: string | null;
  ad_name_locked?: boolean;
  ad_schedule_start?: string | null;
  ad_schedule_end?: string | null;
  leadgenFormId?: string | null;
  creativeTags?: Record<string, string>;
}

export interface AdAccount {
  id: string;
  name: string;
}

export interface Campaign {
  id: string;
  name: string;
  objective?: string | null;
}

export interface AdSet {
  id: string;
  name: string;
  optimizationGoal?: string | null;
  promotedObject?: { page_id?: string; pixel_id?: string; [key: string]: unknown } | null;
  destinationType?: string | null;
  billingEvent?: string | null;
}

export interface FacebookPage {
  id: string;
  name: string;
}

export interface InstagramAccount {
  id: string;
  username: string;
  rawUsername?: string;
}

export interface PartnerInstagramAccount {
  id: string;
  username: string;
  displayName: string;
  sourceInstagramId: string;
  sourceInstagramUsername: string;
  creatorFbPageId: string | null;
}

export interface PartnerFacebookPage {
  id: string;
  name: string;
  username: string;
}

export interface CreativeOption {
  filename: string;
  thumbnail: string;
  dimensions: string;
  aspectRatio: string;
}

export interface ValidationError {
  rowId: string;
  field: string;
  message: string;
}

export interface SubmissionResult {
  rowId: string;
  adName: string;
  success: boolean;
  error?: string;
  metaAdId?: string;
}

export const CTA_OPTIONS = [
  { value: "SHOP_NOW", label: "Shop Now" },
  { value: "APPLY_NOW", label: "Apply Now" },
  { value: "BOOK_NOW", label: "Book Now" },
  { value: "CONTACT_US", label: "Contact Us" },
  { value: "DONATE_NOW", label: "Donate Now" },
  { value: "DOWNLOAD", label: "Download" },
  { value: "GET_OFFER", label: "Get Offer" },
  { value: "GET_QUOTE", label: "Get Quote" },
  { value: "GET_SHOWTIMES", label: "Get Showtimes" },
  { value: "LEARN_MORE", label: "Learn More" },
  { value: "LISTEN_NOW", label: "Listen Now" },
  { value: "ORDER_NOW", label: "Order Now" },
  { value: "PLAY_GAME", label: "Play Game" },
  { value: "REQUEST_TIME", label: "Request Time" },
  { value: "SEE_MENU", label: "See Menu" },
  { value: "SIGN_UP", label: "Sign Up" },
  { value: "SUBSCRIBE", label: "Subscribe" },
  { value: "WATCH_MORE", label: "Watch More" },
] as const;

export const COLLECTION_HEADLINE_MACROS = [
  { value: "{{product.name}}", label: "Product Name" },
  { value: "{{product.brand}}", label: "Brand" },
  { value: "{{product.current_price}}", label: "Current Price" },
  { value: "{{product.price}}", label: "Price" },
  { value: "{{product.description}}", label: "Description" },
  { value: "{{product.short_description}}", label: "Short Description" },
  { value: "{{product.retailer_id}}", label: "Retailer ID" },
  { value: "{{product.url}}", label: "URL" },
] as const;

export const COLLECTION_DESCRIPTION_MACROS = [
  { value: "{{product.current_price}}", label: "Current Price" },
  { value: "{{product.description}}", label: "Description" },
  { value: "{{product.name}}", label: "Product Name" },
  { value: "{{product.brand}}", label: "Brand" },
  { value: "{{product.price}}", label: "Price" },
  { value: "{{product.short_description}}", label: "Short Description" },
  { value: "{{product.retailer_id}}", label: "Retailer ID" },
  { value: "{{product.url}}", label: "URL" },
] as const;

export const CATALOG_HEADLINE_MACROS = [
  { value: "{{product.name}}", label: "Product Name" },
  { value: "{{product.brand}}", label: "Product Brand" },
  { value: "{{product.price}}", label: "Product Price" },
  { value: "{{product.current_price}}", label: "Current Price" },
  { value: "{{product.sale_price}}", label: "Sale Price" },
] as const;

export const createEmptyRow = (inheritAdAccount?: AdRow["adAccount"]): AdRow => ({
  id: crypto.randomUUID(),
  adName: "",
  stage: "Draft",
  adFormat: "Single Image or Video",
  adAccount: inheritAdAccount || null,
  campaign: null,
  adSet: null,
  creatives: { feed: null, vertical: null, horizontal: null },
  carouselData: null,
  collectionData: null,
  catalogAdData: null,
  flexibleData: null,
  catalogId: null,
  catalogName: null,
  productSetId: null,
  productSetName: null,
  facebookPage: null,
  instagramAccount: null,
  partnerFacebookPage: null,
  partnerInstagramAccount: null,
  primaryTexts: ["", "", "", "", ""],
  headlines: ["", "", "", "", ""],
  descriptions: ["", "", "", "", ""],
  productTags: ["", "", "", "", ""],
  url: "",
  displayLink: "",
  utmParameters: "",
  ctaType: "SHOP_NOW",
  status: "PAUSED",
  threadsPageId: "",
  ad_template_id: null,
  ad_name_locked: true,
  ad_schedule_start: null,
  ad_schedule_end: null,
  leadgenFormId: null,
});
